import SettingsRemoteOutlinedIcon from '@mui/icons-material/SettingsRemoteOutlined';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import CastConnectedOutlinedIcon from '@mui/icons-material/CastConnectedOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Reveal } from '@/components/Reveal';

type Replaced = { icon: SvgIconComponent; label: string; note: string };

// Each of these is a capability the platform provides natively — so it's one tool, one login, and
// one line on the bill instead of a separate subscription and vendor per row.
const replaced: Replaced[] = [
  { icon: ConfirmationNumberOutlinedIcon, label: 'PSA & ticketing', note: 'The helpdesk your team runs on' },
  { icon: SettingsRemoteOutlinedIcon, label: 'RMM & patching', note: 'Native agent, signed jobs, patch deployment' },
  { icon: DevicesOtherOutlinedIcon, label: 'Endpoint management / MDM', note: 'Jamf, Intune, and more in one pane' },
  { icon: CastConnectedOutlinedIcon, label: 'Remote control', note: 'Launched straight from a ticket' },
  { icon: BackupOutlinedIcon, label: 'Microsoft 365 backup', note: 'To your own bucket, verified by hash' },
  { icon: LockOutlinedIcon, label: 'Password vault', note: 'Encrypted, rotated, access-logged' },
  { icon: GppGoodOutlinedIcon, label: 'SIEM & EDR', note: 'Security telemetry and threat feeds' },
  { icon: MonitorHeartOutlinedIcon, label: 'Uptime monitoring', note: 'HTTP(S), TLS, and domain expiry' },
  { icon: MenuBookOutlinedIcon, label: 'IT documentation', note: 'Docs linked to devices and tickets' },
  { icon: TrendingUpOutlinedIcon, label: 'CRM & sales', note: 'Pipeline through to a provisioned client' },
  { icon: DrawOutlinedIcon, label: 'E-signature', note: 'Agreements signed in the portal' },
  { icon: InsightsOutlinedIcon, label: 'vCIO & QBR reporting', note: 'Board-ready reviews, generated' },
];

export function Consolidation(): JSX.Element {
  return (
    <section id="consolidation" className="relative overflow-hidden py-16 md:py-24">
      <div
        aria-hidden
        className="animate-drift pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-nexus-purple/[0.08] blur-3xl"
      />
      <div className="container-content relative">
        <Reveal>
          <div className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              One platform, one bill
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              All your tools, one platform. One login. One bill.
            </h2>
            <p className="mt-4 text-white/70">
              Every row below is normally its own subscription, its own login, its own invoice, and
              its own vendor to chase. Nexus is the home for all of them — so the tool sprawl
              collapses into one platform and one vendor, and the total cost comes down with it.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {replaced.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={r.label} delay={(i % 4) * 70}>
                <div className="group flex h-full items-start gap-3.5 rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                    <Icon fontSize="small" />
                  </div>
                  <div>
                    <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{r.label}</h3>
                    <p className="mt-1 text-xs text-white/55">{r.note}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={200}>
          <p className="mt-10 text-center text-sm font-semibold text-white/75">
            One platform · one login · one bill · one vendor.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
